import React, { ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';

// components
import { Text } from '../Text';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton';
import { IconName } from '../Icon/Icon';

import { Size } from 'src/types';

export interface NotificationBannerProps {
  message: ReactNode;
  type?: 'info' | 'warning' | 'success' | 'error';
  testId?: string;
  isClosable?: boolean;
  onClose?: () => void;
  isTitle?: boolean;
  key?: string;
  className?: string;
  notificationIconSize: Size;
  hasBorder?: boolean;
  isNotificationIconFilled?: boolean;
}

const mapColors = {
  error: 'notificationError',
  info: 'notificationInfo',
  success: 'notificationSuccess',
  warning: 'notificationWarning',
};

const mapBorderColors = {
  error: 'notificationErrorBorder',
  info: 'notificationInfoBorder',
  success: 'notificationSuccessBorder',
  warning: 'notificationWarningBorder',
};

const mapIconFillColors = {
  error: 'notificationErrorIcon',
  info: 'notificationInfoIcon',
  success: 'notificationSuccessIcon',
  warning: 'notificationWarningIcon',
};

export const NotificationBanner = ({
  isClosable = true,
  isTitle = false,
  type = 'info',
  message,
  testId,
  className,
  onClose,
  key,
  notificationIconSize = 'small2',
  hasBorder = false,
  isNotificationIconFilled = false,
}: NotificationBannerProps) => {
  const bannerTestId = testId
    ? `${testId}-notification-banner`
    : 'notification-banner';

  const [isVisible, setIsVisible] = useState(true);

  const IconMessage = ({ children }) => (
    <IconContainer>
      <NotificationIcon
        name={IconName[type]}
        size={notificationIconSize}
        isInverted
        color={isNotificationIconFilled ? mapIconFillColors[type] : undefined}
      />
      {children}
    </IconContainer>
  );

  const onCloseHandler = () => {
    onClose?.();
    setIsVisible(false);
  };

  return isVisible ? (
    <BannerContainer
      key={key}
      data-testid={bannerTestId}
      type={type}
      className={className}
      hasBorder={hasBorder}
    >
      {isTitle ? (
        <div>
          <IconMessage>
            <Title variant="big" bold testId={`${bannerTestId}-title`}>
              {type}
            </Title>
          </IconMessage>
          <Message isTitle>{message}</Message>
        </div>
      ) : (
        <div>
          <IconMessage>
            <Message>{message}</Message>
          </IconMessage>
        </div>
      )}
      <div>
        {isClosable && (
          <NotificationButton
            onClick={onCloseHandler}
            name="close"
            color="white"
            size="small2"
            spacing="0"
          />
        )}
      </div>
    </BannerContainer>
  ) : null;
};

type BannerType = 'info' | 'warning' | 'success' | 'error';
interface BannerContainerProps {
  key?: string;
  testId?: string;
  type?: BannerType;
  hasBorder?: boolean;
}

const BannerContainer = styled.article<BannerContainerProps>`
  display: flex;
  justify-content: space-between;
  border-radius: 0.5em;
  padding: 0.82em 1em;
  background-color: ${({ theme, type }) => theme?.colors?.[mapColors[type]]};
  border: ${({ theme, type, hasBorder }) =>
    hasBorder
      ? `solid 0.5px ${theme?.colors?.[mapBorderColors[type]]}`
      : 'none'};

  /* Theming Overrides */
  ${({ theme }) => theme?.overrides?.NotificationBanner};
`;

const NotificationIcon = styled(Icon)`
  margin-right: 0.5em;
  /* Theming Overrides */
  ${({ theme }) => theme?.overrides?.NotificationBanner.NotificationIcon};
`;

const textStyles = css`
  color: ${({ theme }) => theme?.colors?.white};
  margin: 0;

  /* Theming Overrides */
  ${({ theme }) => theme?.overrides?.NotificationBanner?.message}
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
`;

const NotificationButton = styled(IconButton)`
  &:hover,
  &:focus {
    background-color: transparent;
    box-shadow: none;
  }
`;

const Title = styled(Text)`
  text-transform: capitalize;

  /* Theming Overrides */
  ${({ theme }) => theme?.overrides?.NotificationBanner?.message}
  ${textStyles}
`;

const Message = styled(Text)<{ isTitle?: boolean }>`
  /* Theming Overrides */
  ${({ theme }) => theme?.overrides?.NotificationBanner?.message}
  ${textStyles}
  margin-left: ${({ isTitle }) => (isTitle ? '2.3em' : 0)};
`;
